import type { WebhookProviderCatalogEntry } from '@cotera/client/app/v1/webhook-providers';
import { findWebhookProviderCatalogEntry } from '@cotera/client/app/v1/webhook-providers';

/**
 * Everything naming a webhook trigger depends on. Deliberately not the trigger
 * row type: the creation wizard names a trigger that does not exist yet, and
 * both callers must arrive at the same answer from the same facts.
 */
export type WebhookTriggerNamingFacts = {
  providerKey: string | null;
  /** Which of the provider's events it listens for, when the provider has any. */
  eventKey: string | null;
  /** Set only for the `email` provider, and only once the trigger is saved. */
  inboundEmailAddress: string | null;
};

/**
 * What a webhook trigger *is*, in one line, for both the Automations row and
 * the name stored at creation. One rule covers every provider:
 *
 *   email with an address    →  `orders-a7f2k@inbound.cotera.co`
 *   provider with a choice   →  `GitHub · Pull request opened`
 *   provider with one event  →  `Email`
 *   custom webhook           →  `Custom webhook`
 *
 * The event is appended only when the provider offers more than one, because
 * that is the only case where it tells the reader something the provider name
 * did not: a provider with a single event stutters ("Email · Email received").
 *
 * The provider stays in the name even though the row shows its logo beside it,
 * because this same string is the trigger's identity in the Library, in search,
 * and in the workflow builder — none of which draw that logo.
 *
 * Falls back to the raw provider key while the catalog is still loading, since
 * a hand-kept key→name map on the client is exactly the drift the API
 * contract's provider-key list exists to prevent.
 */
export function webhookTriggerDescription(
  facts: WebhookTriggerNamingFacts,
  providers: readonly WebhookProviderCatalogEntry[]
): string {
  // An email trigger is its address — a run per message delivered there — and
  // it outranks the provider name, which would only ever say "Email".
  if (facts.inboundEmailAddress !== null && facts.inboundEmailAddress !== '') {
    return facts.inboundEmailAddress;
  }

  const providerKey =
    facts.providerKey !== null && facts.providerKey !== ''
      ? facts.providerKey
      : null;
  if (providerKey === null) {
    return 'Webhook';
  }

  const entry = findWebhookProviderCatalogEntry(providers, providerKey);
  if (entry === undefined) {
    // `generic` reads as "Custom webhook" via the catalog; without it the bare
    // key would be the least useful thing we could show, so keep the old label.
    return providerKey === 'generic' ? 'Custom webhook' : providerKey;
  }

  const event =
    entry.events.length > 1 && facts.eventKey !== null && facts.eventKey !== ''
      ? entry.events.find((candidate) => candidate.key === facts.eventKey)
      : undefined;

  return event === undefined
    ? entry.displayName
    : `${entry.displayName} · ${event.displayName}`;
}
